import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from 'src/api/rpc/client';

/** Whether this browser can run a WebAuthn ceremony at all. */
export function supportsPasskeys(): boolean {
  return browserSupportsWebAuthn();
}

/**
 * True when the user dismissed or timed out the browser's passkey prompt.
 * The browser reports both as `NotAllowedError`; neither deserves an error
 * message.
 */
export function isPasskeyPromptDismissed(error: unknown): boolean {
  return error instanceof Error && error.name === 'NotAllowedError';
}

/** Sign in with a discoverable passkey (no email needed). */
export function usePasskeyLogin() {
  return useMutation({
    mutationFn: async () => {
      const optionsJSON = await rpcClient.auth.passkeyLoginOptions();
      const response = await startAuthentication({ optionsJSON });
      return rpcClient.auth.passkeyLogin({ response });
    },
  });
}

export function usePasskeys() {
  return useQuery(orpc.auth.passkeys.list.queryOptions());
}

function useInvalidatePasskeys() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: orpc.auth.passkeys.list.key() });
}

/** Create a passkey on this device and register it to the signed-in user. */
export function useRegisterPasskey() {
  const invalidate = useInvalidatePasskeys();
  return useMutation({
    mutationFn: async (params: { name?: string }) => {
      const optionsJSON = await rpcClient.auth.passkeys.registrationOptions();
      const response = await startRegistration({ optionsJSON });
      return rpcClient.auth.passkeys.register({ response, name: params.name });
    },
    onSuccess: invalidate,
  });
}

export function useRenamePasskey() {
  const invalidate = useInvalidatePasskeys();
  return useMutation({
    ...orpc.auth.passkeys.rename.mutationOptions(),
    onSuccess: invalidate,
  });
}

export function useDeletePasskey() {
  const invalidate = useInvalidatePasskeys();
  return useMutation({
    ...orpc.auth.passkeys.delete.mutationOptions(),
    onSuccess: invalidate,
  });
}
